import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css";
import "leaflet-defaulticon-compatibility";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MapComponent({ spaces = [], center = [46.8, -71.2], zoom = 7, height = "400px", singleMarker = null }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    if (mapRef.current) {
      try { mapRef.current.remove(); } catch {}
      mapRef.current = null;
    }

    const timer = setTimeout(() => {
      if (!containerRef.current) return;

      try {
        const map = L.map(containerRef.current, { scrollWheelZoom: true, zoomControl: true }).setView(center, zoom);
        mapRef.current = map;

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 19,
        }).addTo(map);

        const createIcon = (price) => {
          const text = price ? `${price} $` : "";
          const width = Math.max(48, 14 + text.length * 7);
          return L.divIcon({
            className: "campspot-marker",
            html: `<div style="
              display:flex;
              align-items:center;
              justify-content:center;
              background:#059669;
              color:white;
              width:${width}px;
              height:28px;
              border-radius:14px;
              font-weight:700;
              font-size:13px;
              white-space:nowrap;
              border:2px solid white;
              box-shadow:0 2px 10px rgba(0,0,0,.35);
              font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
              line-height:1;
              letter-spacing:-0.01em;
              transform:translate(-50%,-50%);
              position:absolute;
              left:0;
              top:0;
            ">${text}</div>`,
            iconSize: [width, 28],
            iconAnchor: [0, 0],
            popupAnchor: [0, -18],
          });
        };

        const singleIcon = L.divIcon({
          className: "campspot-marker-single",
          html: `<div style="
            background:#059669;
            width:20px;
            height:20px;
            border-radius:50%;
            border:3px solid white;
            box-shadow:0 2px 10px rgba(0,0,0,.4);
          "></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
          popupAnchor: [0, -14],
        });

        if (singleMarker && singleMarker.lat && singleMarker.lng) {
          L.marker([singleMarker.lat, singleMarker.lng], { icon: singleIcon })
            .addTo(map)
            .bindPopup(`<strong>${singleMarker.title || ""}</strong><br/>${singleMarker.address || ""}`)
            .openPopup();
          map.setView([singleMarker.lat, singleMarker.lng], 15);
        } else if (spaces.length > 0) {
          // Group near-identical coordinates so overlapping markers can be spread out
          const groups = new Map();
          spaces.forEach((s) => {
            if (!s.lat || !s.lng) return;
            const key = `${s.lat.toFixed(4)},${s.lng.toFixed(4)}`;
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(s);
          });

          const bounds = [];
          groups.forEach((group) => {
            const n = group.length;
            group.forEach((s, i) => {
              let { lat, lng } = s;
              if (n > 1) {
                // Spread markers on a small circle (~30m radius) so each tag is visible
                const angle = (2 * Math.PI * i) / n;
                const dLat = 0.00028 * Math.cos(angle);
                const dLng = 0.00040 * Math.sin(angle);
                lat = s.lat + dLat;
                lng = s.lng + dLng;
              }
              bounds.push([lat, lng]);
              const price = s.price_per_night ? s.price_per_night.toFixed(0) : "";
              const marker = L.marker([lat, lng], { icon: createIcon(price) }).addTo(map);
              marker.bindPopup(
                `<div style="min-width:180px;font-family:sans-serif;">
                  <strong style="font-size:14px;display:block;margin-bottom:4px;">${s.title}</strong>
                  <span style="color:#059669;font-weight:bold;font-size:13px;">${s.price_per_night?.toFixed(2)} $/nuit</span><br/>
                  <span style="color:#666;font-size:12px;">${s.address || ""}, ${s.city || ""}</span><br/>
                  <a href="/spaces/${s.id}" style="color:#059669;font-size:12px;font-weight:600;text-decoration:none;display:inline-block;margin-top:6px;">Voir les détails &rarr;</a>
                </div>`
              );
            });
          });
          if (bounds.length === 1) {
            map.setView(bounds[0], 12);
          } else if (bounds.length > 1) {
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
          }
        }
      } catch (e) {
        console.warn("Map init error:", e);
      }
    }, 150);

    return () => {
      clearTimeout(timer);
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch {}
        mapRef.current = null;
      }
    };
  }, [JSON.stringify(spaces.map(s => s.id)), center[0], center[1], zoom, singleMarker?.lat, singleMarker?.lng]); // eslint-disable-line

  return (
    <div
      ref={containerRef}
      style={{ height, width: "100%", borderRadius: "1rem", zIndex: 1 }}
      data-testid="map-container"
    />
  );
}
